<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useAttendanceReportStore } from '@/modules/backoffice/stores/attendance.store.ts'

const store = useAttendanceReportStore()

// Dữ liệu được quản lý tập trung tại AttendanceMatrix.vue hoặc Store
const todayStats = computed(() => store.dashboardData?.todayStats || { onTime: 0, late: 0, absent: 0, totalOT: 0, onTimePercent: 0 })
const leaderboards = computed(() => store.dashboardData?.leaderboards || { bestList: [], lateList: [] })
const weeklyTrend = computed(() => store.dashboardData?.weeklyTrend || [])
</script>

<template>
  <div class="mb-5 bg-white border border-slate-200 rounded-lg p-3 px-5 flex flex-wrap xl:flex-nowrap items-center justify-between gap-6">
    <!-- Mini KPIs (Left) -->
    <div class="flex items-center gap-6 divide-x divide-slate-100 flex-1 min-w-max">
      <div class="pr-2">
        <p class="text-[10px] font-bold text-slate-400 p-0 m-0 uppercase tracking-widest mb-1 flex items-center gap-1.5"><span class="w-1.5 h-1.5 rounded-full bg-teal-400"></span> Đúng giờ</p>
        <div class="flex items-baseline gap-2">
          <span class="text-xl font-black text-slate-700 leading-none">{{ todayStats.onTime }}</span>
          <span class="text-[11px] font-bold text-teal-500 bg-teal-50 px-1.5 py-0.5 rounded-md">{{ todayStats.onTimePercent }}% tổng</span>
        </div>
      </div>
      <div class="pl-6">
        <p class="text-[10px] font-bold text-slate-400 p-0 m-0 uppercase tracking-widest mb-1 flex items-center gap-1.5"><span class="w-1.5 h-1.5 rounded-full bg-amber-400"></span> Đi trễ / Về sớm</p>
        <div class="flex items-baseline gap-2">
          <span class="text-xl font-black text-slate-700 leading-none">{{ todayStats.late }}</span>
          <span class="text-[10px] font-semibold text-slate-400">nhân sự</span>
        </div>
      </div>
      <div class="pl-6">
        <p class="text-[10px] font-bold text-slate-400 p-0 m-0 uppercase tracking-widest mb-1 flex items-center gap-1.5"><span class="w-1.5 h-1.5 rounded-full bg-rose-400"></span> Vắng mặt</p>
        <div class="flex items-baseline gap-2">
          <span class="text-xl font-black text-slate-700 leading-none">{{ todayStats.absent }}</span>
          <span class="text-[10px] font-semibold text-slate-400">nhân sự</span>
        </div>
      </div>
      <div class="pl-6">
        <p class="text-[10px] font-bold text-slate-400 p-0 m-0 uppercase tracking-widest mb-1 flex items-center gap-1.5"><span class="w-1.5 h-1.5 rounded-full bg-purple-400"></span> Tổng OT</p>
        <div class="flex items-baseline gap-2">
          <span class="text-xl font-black text-slate-700 leading-none">{{ todayStats.totalOT }}</span>
          <span class="text-[10px] font-semibold text-slate-400">giờ</span>
        </div>
      </div>
    </div>

    <!-- Mini Chart (Sparkline trend - Middle) -->
    <div class="hidden md:flex flex-col flex-1 max-w-[200px] border-l border-slate-100 pl-6 h-[40px] justify-end relative">
      <p class="absolute -top-1 left-6 text-[9px] font-bold text-slate-400 uppercase">Xu hướng đi làm (7 ngày)</p>
      <div class="flex items-end gap-1 w-full h-6 border-b border-slate-100">
        <div v-for="d in weeklyTrend" :key="d.day" class="flex-1 rounded-t-sm transition-colors relative group bg-sky-100 hover:bg-sky-400" :style="{ height: Math.max(d.percent, 10) + '%' }">
          <div class="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block bg-slate-800 text-white text-[9px] py-0.5 px-1.5 rounded whitespace-nowrap z-50 shadow-md">{{ d.percent }}%</div>
        </div>
      </div>
    </div>

    <!-- Micro Leaderboard (Right Corner) -->
    <div class="flex items-center gap-3 border-l border-slate-100 pl-6 shrink-0">
      <div v-if="leaderboards.bestList.length" class="flex items-center gap-2.5 bg-slate-50 border border-slate-100/60 rounded-full pr-3 pl-1 py-1 hover:bg-white transition-colors cursor-default">
        <img :src="leaderboards.bestList[0].avatar" class="w-7 h-7 rounded-full bg-white border border-slate-200 shadow-sm" />
        <div class="flex flex-col justify-center">
          <p class="text-[11px] font-bold text-slate-700 leading-tight">{{ leaderboards.bestList[0].name }}</p>
          <p class="text-[9px] text-teal-600 font-semibold leading-tight flex items-center gap-1"><span class="text-[10px]">🌟</span> Chăm chỉ nhất</p>
        </div>
      </div>

      <div v-if="leaderboards.lateList.length" class="flex items-center gap-2.5 bg-slate-50 border border-slate-100/60 rounded-full pr-3 pl-1 py-1 hover:bg-white transition-colors cursor-default">
        <img :src="leaderboards.lateList[0].avatar" class="w-7 h-7 rounded-full bg-white border border-slate-200 shadow-sm" />
        <div class="flex flex-col justify-center">
          <p class="text-[11px] font-bold text-slate-700 leading-tight">{{ leaderboards.lateList[0].name }}</p>
          <p class="text-[9px] text-rose-500 font-semibold leading-tight flex items-center gap-1"><span class="text-[10px]">⚠️</span> Trễ nhiều nhất</p>
        </div>
      </div>
    </div>
  </div>
</template>
